import AutomaticTestInstructions from '../../components/AutomaticTestInstructions'
import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { 
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  AcademicCapIcon,
  CalendarIcon,
  ArrowRightIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { AppDispatch } from '../../store'
import { register, login } from '../../store/slices/authSlice'
import { driveAPI } from '../../services/api'
import { uploadResume, registerWithResume } from '../../services/resumeAPI'
import CollegeSelect from '../../components/CollegeSelect'
import { College } from '../../services/collegeAPI'
import { Drive } from '../../types'
import { registrationErrorMessage } from '../../utils/registrationErrors'

interface RegistrationForm {
  firstName: string
  lastName: string
  email: string
  password: string
  contactNumber: string
  aadharNumber: string
  college: string
  course: string
  branch: string
  collegePassoutYear: string
  collegePassoutMonth: string
  rollNumber: string
  resume: File | null
  tenthPercentage: string
  twelfthPercentage: string
  collegePercentage: string
}

const DriveRegistrationPage: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [driveInfo, setDriveInfo] = useState<Drive | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRegistering, setIsRegistering] = useState(false)
  const [registrationError, setRegistrationError] = useState('')
  const [registrationStep, setRegistrationStep] = useState('')
  const [driveLoadError, setDriveLoadError] = useState('')
  const registrationInFlight = useRef(false)
  const createdAccountEmail = useRef<string | null>(null)
  const [selectedCollege, setSelectedCollege] = useState<College | null>(null)
  
  // Track last shown toast to prevent duplicate toasts
  const lastToastStatus = useRef<string | null>(null)
  const resumeAccountEmail = useRef<string | null>(null)
  const verifiedCandidateEmail = useRef<string | null>(null)
  const [formData, setFormData] = useState<RegistrationForm>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    contactNumber: '',
    aadharNumber: '',
    college: '',
    course: '',
    branch: '',
    collegePassoutYear: '',
    collegePassoutMonth: '',
    rollNumber: '',
    resume: null,
    tenthPercentage: '',
    twelfthPercentage: '',
    collegePercentage: ''
  })

  useEffect(() => {
    loadDriveInfo()
  }, [linkId])

  // Auto-refresh drive info every 5 seconds to catch status changes
  useEffect(() => {
    const interval = setInterval(() => {
      //console.log('Auto-refreshing drive info...')
      loadDriveInfo()
    }, 5000) // 5 seconds

    return () => clearInterval(interval)
  }, [linkId])

  const loadDriveInfo = async () => {
    if (!linkId) {
      setIsLoading(false)
      toast.error('Invalid registration link')
      return
    }

    try {
      //console.log('Loading drive info for linkId:', linkId)
      
      // Get drive from API with timestamp to prevent caching
      const timestamp = Date.now()
      const randomId = Math.random().toString(36).substring(7)
      const response = await driveAPI.getDriveByLink(linkId + `?t=${timestamp}&r=${randomId}`)
      const drive = response.data
      setDriveLoadError('')
      
      // console.log('Found drive:', drive)
      // console.log('Drive start_date:', drive.start_date)
      // console.log('Drive end_date:', drive.end_date)
      // console.log('Drive status:', drive.status)
      // console.log('Current time:', new Date().toISOString())
      
      // Check if drive is active - only allow registration for active drives
      if (drive.status !== 'active') {
        console.log('Drive is not active, status:', drive.status)
        // Set drive info but mark it as not available for registration
        setDriveInfo({ ...drive, registrationAvailable: false })
        
        // Only show toast if status has changed
        if (lastToastStatus.current !== drive.status) {
          if (drive.status === 'upcoming') {
            toast.error('Drive has not started yet')
          } else if (drive.status === 'completed') {
            toast.error('Drive registration has ended')
          } else {
            toast.error('Drive is not currently accepting registrations')
          }
          lastToastStatus.current = drive.status
        }
        setIsLoading(false)
        return
      }
      
      // Reset toast status when drive becomes active
      lastToastStatus.current = null
      
      setDriveInfo({ ...drive, registrationAvailable: true })
      setIsLoading(false)
      
    } catch (error: any) {
      const message = registrationErrorMessage(error, 'We could not load this test. Check the registration link or try again shortly.')
      setDriveLoadError(message)

      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setFormData(prev => ({
      ...prev,
      resume: file
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (registrationInFlight.current) return
    registrationInFlight.current = true
    setIsRegistering(true)
    setRegistrationError('')
    let step = 'Check your details'
    const showValidation = (message: string, field?: string) => {
      setRegistrationError(message)
      toast.error(message)
      if (field) document.getElementById(field)?.focus()
    }

    try {
      // Validate all sections before creating an account or uploading anything.
      const data = { ...formData, email: formData.email.trim(), firstName: formData.firstName.trim(), lastName: formData.lastName.trim() }
      const requiredFields: Array<[keyof RegistrationForm, string]> = [
        ['firstName', 'first name'], ['lastName', 'last name'], ['email', 'email address'],
        ['password', 'password'], ['contactNumber', 'contact number'], ['aadharNumber', 'Aadhaar number'],
        ['course', 'course'], ['branch', 'branch'], ['collegePassoutYear', 'graduation year'],
        ['collegePassoutMonth', 'graduation month'], ['rollNumber', 'roll number'],
        ['tenthPercentage', '10th percentage'], ['twelfthPercentage', '12th percentage'],
        ['collegePercentage', 'college percentage'],
      ]
      for (const [field, label] of requiredFields) {
        if (!String(data[field] ?? '').trim()) {
          showValidation(`Enter your ${label}.`, field)
          return
        }
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        showValidation('Enter a valid email address.', 'email')
        return
      }
      if (data.password.length < 8) {
        showValidation('Use a password with at least 8 characters.', 'password')
        return
      }
      if (new TextEncoder().encode(data.password).length > 72) {
        showValidation('Your password is too long. Please use a shorter password.', 'password')
        return
      }
      if (!/^[+\d\s()-]+$/.test(data.contactNumber) || data.contactNumber.replace(/\D/g, '').length < 7 || data.contactNumber.replace(/\D/g, '').length > 15) {
        showValidation('Enter a valid contact number, including your country code if needed.', 'contactNumber')
        return
      }
      if (!/^\d{12}$/.test(data.aadharNumber)) {
        showValidation('Aadhaar number must contain exactly 12 digits.', 'aadharNumber')
        return
      }
      if (!selectedCollege) {
        showValidation('Select your college from the college list.')
        return
      }
      for (const [field, label] of [['tenthPercentage', '10th'], ['twelfthPercentage', '12th'], ['collegePercentage', 'College']] as const) {
        const percentage = Number(data[field])
        if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100) {
          showValidation(`${label} percentage must be a number between 0 and 100.`, field)
          return
        }
      }
      if (!data.resume || data.resume.size === 0 || data.resume.size > 5 * 1024 * 1024) {
        showValidation('Upload a non-empty PDF resume no larger than 5 MB.', 'resume')
        return
      }
      if (!/\.pdf$/i.test(data.resume.name) || !['application/pdf', 'application/x-pdf', 'application/octet-stream', ''].includes(data.resume.type)) {
        showValidation('Upload your resume as a PDF file.', 'resume')
        return
      }

      step = 'Check test availability'
      setRegistrationStep(step)
      // Recheck immediately before registration instead of trusting the polling snapshot.
      const { data: latestDrive } = await driveAPI.getDriveByLink(linkId!)
      if (!latestDrive.is_active || latestDrive.status !== 'active') {
        const reason = latestDrive.status === 'upcoming' ? 'Drive has not started yet' : 'Drive registration has ended'
        showValidation(registrationErrorMessage(reason))
        return
      }
      if (latestDrive.registered_candidates >= latestDrive.max_candidates) {
        showValidation(registrationErrorMessage('This drive has reached maximum capacity'))
        return
      }

      if (resumeAccountEmail.current !== data.email) {
        step = 'Create account'
        setRegistrationStep(step)
        if (createdAccountEmail.current !== data.email) {
          const result = await dispatch(register({ email: data.email, password: data.password,
            first_name: data.firstName, last_name: data.lastName,
            phone_number: data.contactNumber.trim(), role: 'candidate' }))
          if (!register.fulfilled.match(result)) {
            throw result.payload
          }
          // Retry later steps without recreating an account created by this form.
          createdAccountEmail.current = data.email
        }
        step = 'Sign in'
        setRegistrationStep(step)
        const result = await dispatch(login({ email: data.email, password: data.password }))
        if (!login.fulfilled.match(result)) throw result.payload
        if (result.payload.user?.role !== 'candidate') throw new Error('Only candidates can upload resumes')
        resumeAccountEmail.current = data.email
        verifiedCandidateEmail.current = result.payload.user.email
      }

      step = 'Upload resume'
      setRegistrationStep(step)
      const resumeUpload = await uploadResume(data.resume, latestDrive.registration_link)

      step = 'Register for test'
      setRegistrationStep(step)
      await registerWithResume({
        registration_link: latestDrive.registration_link,
        registration_data: {
          email: verifiedCandidateEmail.current ?? data.email, firstName: data.firstName, lastName: data.lastName,
          contactNumber: data.contactNumber.trim(), aadharNumber: data.aadharNumber,
          college: selectedCollege.college_name, college_id: selectedCollege.college_id,
          course: data.course.trim(), branch: data.branch.trim(),
          collegePassoutYear: data.collegePassoutYear, collegePassoutMonth: data.collegePassoutMonth,
          rollNumber: data.rollNumber.trim(), tenthPercentage: data.tenthPercentage,
          twelfthPercentage: data.twelfthPercentage, collegePercentage: data.collegePercentage,
          resume: resumeUpload,
        },
      })
      toast.success('Registration successful! You are now logged in.')
      navigate('/dashboard')
    } catch (error) {
      const status = (error as { status?: number; response?: { status?: number } })?.status ?? (error as { response?: { status?: number } })?.response?.status
      if (status === 401) {
        resumeAccountEmail.current = null
        verifiedCandidateEmail.current = null
      }
      const message = `${step}: ${registrationErrorMessage(error, 'We could not complete this step. Please try again.')}`
      setRegistrationError(message)
      toast.error(message)
    } finally {
      setRegistrationStep('')
      setIsRegistering(false)
      registrationInFlight.current = false
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading drive information...</p>
        </div>
      </div>
    )
  }

  if (!driveInfo || !(driveInfo as any).registrationAvailable) {
    const isUpcomingDrive = driveInfo && driveInfo.status === 'upcoming'
    const isCompletedDrive = driveInfo && driveInfo.status === 'completed'
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto">
          <div className={`border rounded-lg p-4 mb-4 ${
            isUpcomingDrive 
              ? 'bg-yellow-50 border-yellow-200' 
              : isCompletedDrive
              ? 'bg-red-50 border-red-200'
              : 'bg-blue-50 border-blue-200'
          }`}>
            <h3 className={`font-semibold mb-2 ${
              isUpcomingDrive ? 'text-yellow-800' : isCompletedDrive ? 'text-red-800' : 'text-blue-800'
            }`}>
              Drive Status
            </h3>
            <p className={`text-sm ${
              isUpcomingDrive ? 'text-yellow-700' : isCompletedDrive ? 'text-red-700' : 'text-blue-700'
            }`}>
              {isUpcomingDrive 
                ? 'This Drive is not active for registration.'
                : isCompletedDrive
                ? 'Drive registration has ended.'
                : 'This Drive is not active for registration.'
              }
            </p>
            
            {/* Only show detailed instructions for upcoming drives */}
            {isUpcomingDrive && (
              <div className="mt-4 text-sm text-yellow-700">
                <div className="space-y-3">
                  {driveInfo.start_date && (
                    <div>
                      <div className="font-semibold">1. Registration Link will gets Active From:</div>
                      <div className="ml-12">{new Date(driveInfo.start_date).toLocaleString()}</div>
                    </div>
                  )}
                  <div>
                    <div className="font-semibold">2. Assessment will starts immediate after registration</div>
                    <div className="ml-12">Just wait few seconds after register and login.</div>
                  </div>
                  <div>
                    <div className="font-semibold">3. You will be moved to Your Dashboard</div>
                    <div className="ml-12">to Access the Assessment.</div>
                  </div>
                  <div>
                    <div className="font-semibold">4. Start Your Test Just after Register and Login.</div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {driveInfo ? <div className="my-4 rounded-lg border border-gray-200 bg-white p-4 text-left"><AutomaticTestInstructions configs={driveInfo.assessment_configs} /></div> : null}
          {driveLoadError ? <p role="alert" className="mb-4 text-sm text-red-700">{driveLoadError}</p> : null}
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="btn btn-outline w-full"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Drive Registration
          </h1>
          <p className="text-gray-600">Register for the recruitment drive</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Drive Information - Left Side */}
          <div className="lg:w-96 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <CalendarIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Drive Details
                </h2>
                
                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{driveInfo.title}</h3>
                    <p className="text-gray-600 text-sm mt-1">{driveInfo.description}</p>
                  </div>

                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span className="capitalize">{driveInfo.college_type}</span>
                    {driveInfo.role && (
                      <>
                        <span>•</span>
                        <span className="font-medium">{driveInfo.role}</span>
                      </>
                    )}
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="w-4 h-4 mr-2" />
                    <span>Registration ends: {new Date(driveInfo.end_date).toLocaleString()}</span>
                  </div>

                </div>
              </div>

              {/* Assessment Summary */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <ClockIcon className="w-5 h-5 mr-2 text-green-600" />
                  Test Structure
                </h3>
                <div className="space-y-2">
                  {Object.entries(driveInfo.assessment_configs).map(([type, config]: [string, any]) => (
                    <div key={type} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 capitalize">{type.replace(/-/g, ' ')}</span>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">{config.duration_minutes} min</span>
                        <span className="text-gray-500 mx-1">•</span>
                        <span className="text-gray-600">{config.num_questions} questions</span>
                      </div>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-2 flex items-center justify-between font-semibold">
                    <span className="text-gray-900">Total Duration</span>
                    <span className="text-blue-600">{driveInfo.total_duration_minutes} minutes</span>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4"><AutomaticTestInstructions configs={driveInfo.assessment_configs} /></div>
            </div>
          </div>

          {/* Registration Form - Right Side */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <UserIcon className="w-5 h-5 mr-2 text-green-600" />
                Student Registration
              </h2>

            <form onSubmit={handleSubmit} noValidate className="space-y-4 max-w-2xl">
              {registrationError ? <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">{registrationError}</p> : null}
              {driveLoadError ? <p role="status" className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">{driveLoadError}</p> : null}
              <p role="status" aria-live="polite" className="text-sm text-gray-600">{registrationStep}</p>
              <fieldset disabled={isRegistering} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Minimum 8 characters"
                />
                <p className="mt-1 text-xs text-gray-500">
                  This will be your login password for accessing tests
                </p>
              </div>

              <div>
                <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Number *
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  required
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="aadharNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Aadhar Number *
                </label>
                <input
                  type="text"
                  id="aadharNumber"
                  name="aadharNumber"
                  required
                  value={formData.aadharNumber}
                  onChange={handleInputChange}
                  maxLength={12}
                  pattern="[0-9]{12}"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter 12-digit Aadhar number"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter your 12-digit Aadhar number
                </p>
              </div>

              {/* 10th Percentage */}
              <div>
                <label htmlFor="tenthPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                  10th Percentage *
                </label>
                <input
                  type="number"
                  id="tenthPercentage"
                  name="tenthPercentage"
                  required
                  min={0}
                  max={100}
                  step="0.01"
                  value={formData.tenthPercentage}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none'
                  } as React.CSSProperties}
                />
              </div>

              {/* 12th Percentage */}
              <div>
                <label htmlFor="twelfthPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                  12th Percentage *
                </label>
                <input
                  type="number"
                  id="twelfthPercentage"
                  name="twelfthPercentage"
                  required
                  min={0}
                  max={100}
                  step="0.01"
                  value={formData.twelfthPercentage}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none'
                  } as React.CSSProperties}
                />
              </div>

              {/* College/University Percentage */}
              <div>
                <label htmlFor="collegePercentage" className="block text-sm font-medium text-gray-700 mb-1">
                  College/University Percentage *
                </label>
                <input
                  type="number"
                  id="collegePercentage"
                  name="collegePercentage"
                  required
                  min={0}
                  max={100}
                  step="0.01"
                  value={formData.collegePercentage}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none'
                  } as React.CSSProperties}
                />
              </div>

              <div>
                <CollegeSelect id="college" value={selectedCollege} onChange={college => {
                  setSelectedCollege(college)
                  setFormData(previous => ({ ...previous, college: college?.college_name || '' }))
                }} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="course" className="block text-sm font-medium text-gray-700 mb-1">
                    Course/Program *
                  </label>
                  <input
                    type="text"
                    id="course"
                    name="course"
                    required
                    value={formData.course}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="branch" className="block text-sm font-medium text-gray-700 mb-1">
                    Branch *
                  </label>
                  <input
                    type="text"
                    id="branch"
                    name="branch"
                    required
                    value={formData.branch}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="collegePassoutYear" className="block text-sm font-medium text-gray-700 mb-1">
                    College Passout Year *
                  </label>
                  <select
                    id="collegePassoutYear"
                    name="collegePassoutYear"
                    required
                    value={formData.collegePassoutYear}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Year</option>
                    {(() => {
                      const years: number[] = []
                      for (let y = 2018; y <= 2028; y++) years.push(y)
                      return years.map(y => (
                        <option key={y} value={String(y)}>{y}</option>
                      ))
                    })()}
                  </select>
                </div>
                {formData.collegePassoutYear && (
                  <div>
                    <label htmlFor="collegePassoutMonth" className="block text-sm font-medium text-gray-700 mb-1">
                      Month of Passing *
                    </label>
                    <select
                      id="collegePassoutMonth"
                      name="collegePassoutMonth"
                      required
                      value={formData.collegePassoutMonth}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Month</option>
                      <option value="January">January</option>
                      <option value="February">February</option>
                      <option value="March">March</option>
                      <option value="April">April</option>
                      <option value="May">May</option>
                      <option value="June">June</option>
                      <option value="July">July</option>
                      <option value="August">August</option>
                      <option value="September">September</option>
                      <option value="October">October</option>
                      <option value="November">November</option>
                      <option value="December">December</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="rollNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Roll Number/Student ID *
                </label>
                <input
                  type="text"
                  id="rollNumber"
                  name="rollNumber"
                  required
                  value={formData.rollNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Resume Upload */}
              <div>
                <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-1">
                  Resume/CV *
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    accept=".pdf,application/pdf"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  {formData.resume && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center text-sm text-green-700">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">{formData.resume.name}</span>
                        <span className="ml-2 text-green-600">
                          ({(formData.resume.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Upload your resume in PDF format only (max 5MB)
                </p>
              </div>

              <button
                type="submit"
                disabled={isRegistering || driveInfo.registered_candidates >= driveInfo.max_candidates}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRegistering ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {registrationStep || 'Registering...'}
                  </>
                ) : (
                  <>
                    <span>Register & Login</span>
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>

              {driveInfo.registered_candidates >= driveInfo.max_candidates && (
                <p className="text-red-600 text-sm text-center">
                  Maximum candidates reached. Registration is closed.
                </p>
              )}
              </fieldset>
            </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DriveRegistrationPage
